import React, { useState } from 'react';
import { brandColors, styles } from '../styles/clientManagementStyles';
import { shallowEqualIgnoringFunctions } from '../utils/memoCompare';
import { colors as theme } from '../theme';

const ClientNutritionTab = ({ selectedClient, clientNutritionData, clientWeightHistory, nutritionStatus = 'loading', onRetry }) => {
  const [nutritionChartPeriod, setNutritionChartPeriod] = useState('3M');

  const nd = clientNutritionData;
  const profile = nd?.profile;
  const dietary = nd?.dietary;
  const health = nd?.health;
  const glp1 = nd?.glp1;
  const sub = nd?.subscription;
  const nutritionWeightLogs = nd?.weightLogs ?? [];
  // Fall back to fitness check-in weights when no Nutrition weight logs exist
  const fitnessWeightLogs = (clientWeightHistory || []).map(w => ({
    weight_kg: w.weight,
    logged_at: w.date instanceof Date ? w.date.toISOString() : w.date,
  }));
  const allLogs = nutritionWeightLogs.length > 0 ? nutritionWeightLogs : fitnessWeightLogs;
  const doseLogs = nd?.doseLogs ?? [];

  // Period filter
  const periodDays = { '2W': 14, '1M': 30, '3M': 90, 'All': Infinity };
  const cutoff = Date.now() - (periodDays[nutritionChartPeriod] ?? 90) * 86400000;
  const logs = nutritionChartPeriod === 'All' ? allLogs : allLogs.filter(l => new Date(l.logged_at).getTime() >= cutoff);
  const chartLogs = logs.length >= 2 ? logs : allLogs.slice(-2);

  // SVG chart helpers
  const W = 760, H = 180, PAD = { top: 16, bottom: 36, left: 44, right: 12 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const chartReady = chartLogs.length >= 2;
  let pathD = '', fillD = '', trendPath = '', xLabels = [], yTicks = [];

  if (chartReady) {
    const minW = Math.min(...chartLogs.map(l => l.weight_kg));
    const maxW = Math.max(...chartLogs.map(l => l.weight_kg));
    const range = maxW - minW || 1;
    const padV = range * 0.15;
    const yMin = minW - padV, yMax = maxW + padV;

    const t0 = new Date(chartLogs[0].logged_at).getTime();
    const t1 = new Date(chartLogs[chartLogs.length - 1].logged_at).getTime();
    const tRange = t1 - t0 || 1;

    const toX = t => PAD.left + ((t - t0) / tRange) * innerW;
    const toY = w => PAD.top + (1 - (w - yMin) / (yMax - yMin)) * innerH;

    const pts = chartLogs.map(l => ({ x: toX(new Date(l.logged_at).getTime()), y: toY(l.weight_kg), w: l.weight_kg, t: new Date(l.logged_at).getTime() }));

    pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    fillD = `${pathD} L${pts[pts.length - 1].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} L${pts[0].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`;

    // Linear trend
    const xs = pts.map(p => p.t), ys = pts.map(p => p.w);
    const n = xs.length, mx = xs.reduce((a, b) => a + b, 0) / n, my = ys.reduce((a, b) => a + b, 0) / n;
    const num = xs.reduce((s, x, i) => s + (x - mx) * (ys[i] - my), 0);
    const den = xs.reduce((s, x) => s + (x - mx) ** 2, 0);
    const slope = den ? num / den : 0, intercept = my - slope * mx;
    const ty0 = toY(slope * t0 + intercept), ty1 = toY(slope * t1 + intercept);
    trendPath = `M${PAD.left.toFixed(1)},${ty0.toFixed(1)} L${(PAD.left + innerW).toFixed(1)},${ty1.toFixed(1)}`;

    // Y axis ticks
    const step = range <= 2 ? 0.5 : range <= 5 ? 1 : range <= 10 ? 2 : 5;
    const tickStart = Math.ceil(yMin / step) * step;
    for (let v = tickStart; v <= yMax; v = Math.round((v + step) * 10) / 10) {
      yTicks.push({ v, y: toY(v) });
    }

    // X axis labels (up to 5)
    const labelCount = Math.min(5, chartLogs.length);
    const step2 = Math.floor((chartLogs.length - 1) / (labelCount - 1)) || 1;
    for (let i = 0; i < chartLogs.length; i += step2) {
      const l = chartLogs[i];
      const d = new Date(l.logged_at);
      xLabels.push({ x: toX(d.getTime()), label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) });
    }
  }

  const latest = allLogs.length ? allLogs[allLogs.length - 1] : null;
  const first = allLogs.length ? allLogs[0] : null;
  const totalChange = latest && first ? (latest.weight_kg - first.weight_kg).toFixed(1) : null;
  const changeColor = totalChange < 0 ? theme.success : totalChange > 0 ? theme.danger : brandColors.silver;

  const goalLabel = { weight_loss: 'Weight Loss', lose_weight: 'Weight Loss', muscle_gain: 'Muscle Gain', build_muscle: 'Build Muscle', body_recomp: 'Body Recomposition', glp1_support: 'GLP-1 Support', maintenance: 'Maintenance', maintain: 'Maintenance', get_stronger: 'Get Stronger', improve_fitness: 'Improve Fitness', type2_diabetes: 'Type 2 Diabetes' };

  // Calculate nutrition targets from Fitness profile (Mifflin-St Jeor)
  const calcWeight = allLogs.length ? allLogs[allLogs.length - 1].weight_kg : selectedClient?.weight;
  const calcHeight = selectedClient?.height;
  const calcAge = parseInt(selectedClient?.age) || null;
  const calcSex = selectedClient?.sex?.toLowerCase();
  const activeGoal = profile?.goal_type || selectedClient?.primary_goal;
  let calcTargets = null;
  if (calcWeight && calcHeight && calcAge) {
    const bmr = calcSex === 'male'
      ? 10 * calcWeight + 6.25 * calcHeight - 5 * calcAge + 5
      : calcSex === 'female'
      ? 10 * calcWeight + 6.25 * calcHeight - 5 * calcAge - 161
      : 10 * calcWeight + 6.25 * calcHeight - 5 * calcAge - 78;
    const freqNum = parseFloat(String(selectedClient?.exercise_frequency ?? '3').split('-')[0]) || 3;
    // 5 days/week is very active and warrants a higher multiplier than 4 days
    const actMult = freqNum <= 1 ? 1.2 : freqNum <= 2 ? 1.375 : freqNum <= 4 ? 1.55 : freqNum <= 5 ? 1.725 : freqNum <= 6 ? 1.725 : 1.9;
    const tdee = Math.round(bmr * actMult);
    const isGlp1 = ['glp1_support'].includes(activeGoal);
    const isLoss = ['weight_loss', 'lose_weight'].includes(activeGoal);
    const isGain = ['muscle_gain', 'build_muscle'].includes(activeGoal);
    const isRecomp = ['body_recomp'].includes(activeGoal);
    const isStrength = ['get_stronger'].includes(activeGoal);
    // recomp: eat at TDEE (no surplus) but with high protein to support muscle gain
    // strength: slight surplus to support progressive overload, moderate protein
    // improve_fitness / maintain: TDEE, maintenance protein
    const cals = isGlp1 ? Math.min(Math.max(1200, Math.round(bmr)), 1600) : isLoss ? tdee - 400 : isGain ? tdee + 150 : isStrength ? tdee + 100 : tdee;
    const proteinPerKg = isGlp1 ? 2.0 : isGain ? 2.2 : isRecomp ? 2.2 : isLoss ? 2.2 : isStrength ? 2.0 : 1.8;
    const protein = Math.round(calcWeight * proteinPerKg);
    calcTargets = { bmr: Math.round(bmr), tdee, cals, protein };
  }

  return (
    <div style={styles.section}>

      {nutritionStatus === 'loading' && <p role="status">Loading Nutrition…</p>}
      {nutritionStatus === 'error' && <div role="alert">
        <p>Couldn't load Nutrition.</p>
        <button onClick={onRetry} style={styles.viewBtn}>Retry</button>
      </div>}
      {/* ── No Nutrition Profile ──────────────────────────── */}
      {nutritionStatus === 'loaded' && !profile && (
        <div style={{ ...styles.noProgram, padding: '60px 0' }}>
          <span style={{ fontSize: '48px' }}>🥗</span>
          <p style={{ color: brandColors.silver, marginTop: '12px' }}>This client isn't using Nutrition yet</p>
          <p style={{ color: brandColors.silver }}>They can open Nutrition in Cipher and follow the setup questions to get started.</p>
        </div>
      )}

      {nutritionStatus === 'loaded' && profile && (
        <>
          {/* ── Subscription badge ─────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Nutrition Overview</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {sub && (
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', backgroundColor: sub.tier === 'bundle' ? brandColors.gold + '20' : '#32D74B20', color: sub.tier === 'bundle' ? brandColors.gold : theme.success, border: `1px solid ${sub.tier === 'bundle' ? brandColors.gold : theme.success}` }}>
                  {sub.tier === 'bundle' ? '⚡ Bundle' : sub.tier === 'free' ? 'Free' : '✓ Nutrition'}
                </span>
              )}
              {sub?.current_period_end && (
                <span style={{ fontSize: '12px', color: brandColors.silver }}>
                  {sub.cancel_at_period_end ? 'Cancels' : 'Renews'} {new Date(sub.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>

          {/* ── Onboarding summary ─────────────────────────── */}
          <div style={{ ...styles.card, padding: '20px', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: health?.conditions?.length || dietary?.diet_type ? '20px' : 0 }}>
              {[
                { label: 'GOAL', value: goalLabel[profile.goal_type] ?? profile.goal_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) ?? '—' },
                { label: 'TARGET WEIGHT', value: profile.goal_weight_kg ? `${profile.goal_weight_kg} kg` : 'Not set' },
                { label: 'DIET TYPE', value: dietary?.diet_type ? dietary.diet_type.replace(/_/g, ' ') : 'Not set' },
                { label: 'HEIGHT', value: profile.height_cm ? `${profile.height_cm} cm` : '—' },
                { label: 'PLAN STARTED', value: profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' },
              ].map(({ label, value }) => (
                <div key={label} style={styles.infoItem}>
                  <span style={styles.infoLabel}>{label}</span>
                  <span style={{ ...styles.infoValue, fontSize: '14px' }}>{value}</span>
                </div>
              ))}
            </div>

            {health?.conditions?.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(255,69,58,0.08)', borderRadius: '8px', border: '1px solid rgba(255,69,58,0.2)' }}>
                <span style={{ fontSize: '11px', color: theme.danger, fontWeight: '700', display: 'block', marginBottom: '6px' }}>⚠ HEALTH CONDITIONS</span>
                <span style={{ color: brandColors.white, fontSize: '13px' }}>{health.conditions.map(c => c.replace(/_/g, ' ')).join(', ')}</span>
              </div>
            )}

            {glp1 && (
              <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(212,175,55,0.08)', borderRadius: '8px', border: `1px solid ${brandColors.gold}40` }}>
                <span style={{ fontSize: '11px', color: brandColors.gold, fontWeight: '700', display: 'block', marginBottom: '6px' }}>💊 GLP-1 MEDICATION</span>
                <span style={{ color: brandColors.white, fontSize: '13px' }}>
                  {glp1.medication} · {(glp1.dose_stage || '').replace(/_/g, ' ')} phase
                  {glp1.months_on_med ? ` · ${glp1.months_on_med.replace(/_/g, ' ')}` : ''}
                </span>
                {glp1.challenges?.length > 0 && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: brandColors.silver }}>
                    Challenges: {glp1.challenges.map(c => c.replace(/_/g, ' ')).join(', ')}
                  </div>
                )}
              </div>
            )}

            {dietary?.foods_to_avoid?.length > 0 && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: brandColors.silver }}>
                <strong style={{ color: brandColors.white }}>Avoid:</strong> {dietary.foods_to_avoid.join(', ')}
              </div>
            )}
          </div>

          {/* ── Calculated Targets ─────────────────────────── */}
          {calcTargets && (
            <div style={{ ...styles.card, padding: '20px', marginBottom: '24px', border: `1px solid ${brandColors.gold}30`, background: 'linear-gradient(135deg, rgba(212,175,55,0.04) 0%, rgba(10,14,39,1) 100%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <span style={{ fontSize: '11px', color: brandColors.gold, fontWeight: '700', letterSpacing: '1px' }}>CALCULATED TARGETS</span>
                <span style={{ fontSize: '11px', color: brandColors.silver }}>Mifflin-St Jeor · fitness profile</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '16px' }}>
                {[
                  { label: 'BMR', value: `${calcTargets.bmr.toLocaleString()} kcal`, sub: 'base metabolic rate' },
                  { label: 'TDEE', value: `${calcTargets.tdee.toLocaleString()} kcal`, sub: 'total daily expenditure' },
                  { label: 'CALORIES', value: `${calcTargets.cals.toLocaleString()} kcal`, sub: goalLabel[activeGoal] ?? 'maintenance', highlight: true },
                  { label: 'PROTEIN', value: `${calcTargets.protein}g / day`, sub: `${(calcTargets.protein / calcWeight).toFixed(1)}g per kg`, highlight: true },
                ].map(({ label, value, sub, highlight }) => (
                  <div key={label} style={styles.infoItem}>
                    <span style={styles.infoLabel}>{label}</span>
                    <span style={{ ...styles.infoValue, fontSize: '14px', color: highlight ? brandColors.gold : brandColors.white }}>{value}</span>
                    <span style={{ fontSize: '11px', color: brandColors.silver, marginTop: '2px', display: 'block' }}>{sub}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Weight Chart ───────────────────────────────── */}
          <div style={{ ...styles.card, padding: '20px', marginBottom: '24px' }}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', color: brandColors.silver, fontWeight: '700', letterSpacing: '0.5px', marginBottom: '4px' }}>CURRENT WEIGHT</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <span style={{ fontSize: '32px', fontWeight: '700', color: brandColors.white, lineHeight: 1 }}>
                    {latest ? `${latest.weight_kg} kg` : '—'}
                  </span>
                  {totalChange !== null && (
                    <span style={{ fontSize: '14px', fontWeight: '600', color: changeColor }}>
                      {totalChange > 0 ? '+' : ''}{totalChange} kg total
                    </span>
                  )}
                </div>
                {latest && (
                  <div style={{ fontSize: '12px', color: brandColors.silver, marginTop: '4px' }}>
                    Updated {new Date(latest.logged_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
              {/* Period tabs */}
              <div style={{ display: 'flex', gap: '4px', background: brandColors.mediumGray, padding: '3px', borderRadius: '20px', border: `1px solid ${brandColors.lightGray}` }}>
                {['2W', '1M', '3M', 'All'].map(p => (
                  <button key={p} onClick={() => setNutritionChartPeriod(p)} style={{ padding: '5px 14px', borderRadius: '14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', backgroundColor: nutritionChartPeriod === p ? brandColors.gold : 'transparent', color: nutritionChartPeriod === p ? brandColors.darkGray : brandColors.silver }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats row */}
            {allLogs.length >= 2 && (
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${brandColors.lightGray}` }}>
                {[
                  { label: 'Starting', value: `${first.weight_kg} kg` },
                  { label: 'Lowest', value: `${Math.min(...allLogs.map(l => l.weight_kg))} kg` },
                  { label: 'Entries', value: allLogs.length },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: '11px', color: brandColors.silver, fontWeight: '600', marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '15px', color: brandColors.white, fontWeight: '600' }}>{value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* SVG chart */}
            {chartReady ? (
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
                <defs>
                  <linearGradient id="nutWGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={theme.gold} stopOpacity="0.35" />
                    <stop offset="100%" stopColor={theme.gold} stopOpacity="0" />
                  </linearGradient>
                </defs>
                {/* Y grid lines */}
                {yTicks.map(({ v, y }) => (
                  <g key={v}>
                    <line x1={PAD.left} y1={y} x2={PAD.left + innerW} y2={y} stroke={brandColors.lightGray} strokeWidth="1" strokeDasharray="3,4" opacity="0.5" />
                    <text x={PAD.left - 6} y={y + 4} textAnchor="end" fill={brandColors.silver} fontSize="10">{v}</text>
                  </g>
                ))}
                {/* Dose change markers */}
                {doseLogs.map((d, i) => {
                  const t = new Date(d.date_started).getTime();
                  const t0 = chartLogs.length ? new Date(chartLogs[0].logged_at).getTime() : 0;
                  const t1 = chartLogs.length ? new Date(chartLogs[chartLogs.length - 1].logged_at).getTime() : 1;
                  const tR = t1 - t0 || 1;
                  if (t < t0 || t > t1) return null;
                  const x = PAD.left + ((t - t0) / tR) * innerW;
                  return (
                    <g key={i}>
                      <line x1={x} y1={PAD.top} x2={x} y2={PAD.top + innerH} stroke={brandColors.gold} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7" />
                      <circle cx={x} cy={PAD.top + 8} r="5" fill={brandColors.gold} />
                      <text x={x} y={PAD.top + 8} textAnchor="middle" fill={brandColors.darkGray} fontSize="8" fontWeight="700">💊</text>
                    </g>
                  );
                })}
                {/* Fill */}
                <path d={fillD} fill="url(#nutWGrad)" />
                {/* Line */}
                <path d={pathD} fill="none" stroke={theme.gold} strokeWidth="2.5" strokeLinejoin="round" />
                {/* Trend */}
                <path d={trendPath} fill="none" stroke={theme.success} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.8" />
                {/* Data points */}
                {chartLogs.map((l, i) => {
                  const t = new Date(l.logged_at).getTime();
                  const t0 = new Date(chartLogs[0].logged_at).getTime();
                  const t1 = new Date(chartLogs[chartLogs.length - 1].logged_at).getTime();
                  const tR = t1 - t0 || 1;
                  const x = PAD.left + ((t - t0) / tR) * innerW;
                  const minW2 = Math.min(...chartLogs.map(ll => ll.weight_kg));
                  const maxW2 = Math.max(...chartLogs.map(ll => ll.weight_kg));
                  const rng = maxW2 - minW2 || 1;
                  const pV = rng * 0.15;
                  const y = PAD.top + (1 - (l.weight_kg - (minW2 - pV)) / ((maxW2 + pV) - (minW2 - pV))) * innerH;
                  const isFirst = i === 0, isLast = i === chartLogs.length - 1;
                  return (isFirst || isLast) ? (
                    <circle key={i} cx={x} cy={y} r="5" fill={theme.gold} stroke={brandColors.darkGray} strokeWidth="2" />
                  ) : null;
                })}
                {/* X labels */}
                {xLabels.map(({ x, label }, i) => (
                  <text key={i} x={x} y={H - 4} textAnchor="middle" fill={brandColors.silver} fontSize="10">{label}</text>
                ))}
                {/* Legend */}
                <g transform={`translate(${PAD.left + innerW - 100},${PAD.top})`}>
                  <line x1="0" y1="6" x2="16" y2="6" stroke={theme.success} strokeWidth="1.5" strokeDasharray="5,3" />
                  <text x="20" y="10" fill={brandColors.silver} fontSize="9">Trend</text>
                </g>
              </svg>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: brandColors.silver }}>
                {allLogs.length === 0 ? 'No weight entries yet' : 'Need at least 2 entries to show chart'}
              </div>
            )}
          </div>

          {/* ── Dose log ───────────────────────────────────── */}
          {doseLogs.length > 0 && (
            <div style={{ ...styles.card, padding: '20px' }}>
              <h3 style={{ ...styles.sectionTitle, fontSize: '14px', marginBottom: '16px' }}>💊 GLP-1 Dose History</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {doseLogs.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: brandColors.mediumGray, borderRadius: '8px', border: `1px solid ${brandColors.lightGray}` }}>
                    <div>
                      <span style={{ color: brandColors.white, fontWeight: '600', fontSize: '14px' }}>{d.medication}</span>
                      <span style={{ color: brandColors.silver, fontSize: '12px', marginLeft: '8px' }}>{d.dose_mg} mg</span>
                    </div>
                    <span style={{ color: brandColors.silver, fontSize: '12px' }}>
                      {new Date(d.date_started).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(ClientNutritionTab, shallowEqualIgnoringFunctions);
